/** Shared engagement / prospect stage helpers (mirrors backend studio_packets). */

export const ENGAGEMENT_STAGES = [
  "outreach",
  "met",
  "proposal",
  "invoice",
  "active",
  "closeout",
  "archive",
];

export const PROSPECT_STAGES = new Set(["outreach", "met", "proposal", "invoice"]);
export const ENGAGEMENT_BUCKET_STAGES = new Set(["active", "closeout", "archive"]);

export const STAGE_LABEL = {
  outreach: "Inquiry",
  met: "Met",
  proposal: "Scope",
  invoice: "Scope + Invoice",
  active: "Active",
  closeout: "Closeout",
  archive: "Archived",
  // legacy
  lead: "Inquiry",
};

/**
 * Board columns for inbound pipeline.
 * Flow: Inquiry → Met → Scope + Invoice → (paid) Studio Active; stale / unpaid → Archived.
 */
export const OUTREACH_BOARD_COLUMNS = [
  { key: "outreach", label: "Inquiry", stages: ["outreach"] },
  { key: "met", label: "Met", stages: ["met"] },
  { key: "scope", label: "Scope + Invoice", stages: ["proposal", "invoice"] },
  { key: "archive", label: "Archived", stages: ["archive"] },
];

/** Active pipeline only (home funnel / in-motion counts). */
export const OUTREACH_ACTIVE_COLUMNS = OUTREACH_BOARD_COLUMNS.filter((c) => c.key !== "archive");

export const MONTHLY_ENGAGEMENT_GOAL = 3;

/** Days without update in Invoice before auto-archive. */
export const INVOICE_STALE_ARCHIVE_DAYS = 7;

const STAGE_ALIASES = { lead: "outreach" };

export function normalizeEngagementStage(raw, { defaultStage = "active" } = {}) {
  if (raw == null || String(raw).trim() === "") return defaultStage;
  const value = STAGE_ALIASES[String(raw).trim().toLowerCase()] || String(raw).trim().toLowerCase();
  return ENGAGEMENT_STAGES.includes(value) ? value : defaultStage;
}

export function isProspectStage(stage) {
  // Empty/unknown → active (legacy engagement), so not a prospect.
  return PROSPECT_STAGES.has(normalizeEngagementStage(stage));
}

export function isEngagementBucketStage(stage) {
  return ENGAGEMENT_BUCKET_STAGES.has(normalizeEngagementStage(stage));
}

export function isBarterClient(client) {
  if (!client || typeof client !== "object") return false;
  const type = String(client.compensation_type || "cash").toLowerCase();
  return type === "barter" || type === "in_kind" || type === "inkind";
}

/** UI label for compensation / exchange type. */
export function compensationExchangeLabel(compensationType) {
  const type = String(compensationType || "cash").toLowerCase();
  if (type === "barter" || type === "in_kind" || type === "inkind") return "In-kind";
  return "Invoice";
}

/** Invoice pickers: non-archived clients, including prospects (cash only). */
export function isInvoiceEligibleClient(client) {
  if (!client || client.archived) return false;
  if (isBarterClient(client)) return false;
  const status = client.status || "active";
  if (status === "archived" || status === "offboarded") return false;
  const stage = normalizeEngagementStage(client.engagement_stage);
  return stage !== "archive";
}

export const PROSPECT_STAGE_OPTIONS = [
  ["outreach", "Inquiry"],
  ["met", "Met"],
  ["proposal", "Scope + Invoice"],
  ["invoice", "Scope + Invoice"],
];

/** Stage keys shown in filters. */
export const OUTREACH_FILTER_OPTIONS = [
  ["all", "All"],
  ["outreach", "Inquiry"],
  ["met", "Met"],
  ["scope", "Scope + Invoice"],
  ["archive", "Archived"],
];

/** Map a client (or stage string) to an outreach board column key. */
export function outreachBoardColumnKey(clientOrStage) {
  if (clientOrStage && typeof clientOrStage === "object") {
    if (isOutreachArchived(clientOrStage)) return "archive";
    const s = normalizeEngagementStage(clientOrStage.engagement_stage, {
      defaultStage: "outreach",
    });
    // Scope + Invoice share one board column (barter never sits on invoice alone).
    if (s === "proposal" || s === "invoice") return "scope";
    if (s === "met") return "met";
    if (isProspectStage(s)) return "outreach";
    return null;
  }
  const s = normalizeEngagementStage(clientOrStage, { defaultStage: "outreach" });
  if (s === "archive") return "archive";
  if (s === "proposal" || s === "invoice") return "scope";
  if (s === "met") return "met";
  return "outreach";
}

/** True when client belongs on the outreach Archived stage. */
export function isOutreachArchived(client) {
  if (!client) return false;
  const status = client.status || (client.archived ? "archived" : "active");
  if (status === "archived" || status === "offboarded" || client.archived) return true;
  return normalizeEngagementStage(client.engagement_stage) === "archive";
}

export const ENGAGEMENT_STAGE_OPTIONS = [
  ["active", "Active"],
  ["closeout", "Closeout"],
  ["archive", "Archived"],
];

export const SCOPE_REVIEW_STATUS_LABEL = {
  pending: "Awaiting response",
  accepted: "Accepted",
  changes_requested: "Changes requested",
};

export function scopeReviewStatusLabel(status, { barter = false } = {}) {
  if (barter && status === "accepted") return "Signed";
  if (barter && status === "pending") return "Awaiting signature";
  return SCOPE_REVIEW_STATUS_LABEL[status] || status;
}

/** Scope column / stage label — Agreement for barter, Scope for cash. */
export function prospectScopeLabel(clientOrBarter) {
  if (clientOrBarter === true) return "Agreement";
  if (clientOrBarter && typeof clientOrBarter === "object" && isBarterClient(clientOrBarter)) {
    return "Agreement";
  }
  return STAGE_LABEL.proposal || "Scope";
}

function clientTimestampMs(value) {
  if (!value) return 0;
  const t = Date.parse(String(value));
  return Number.isFinite(t) ? t : 0;
}

/** Newest first by created_at (outreach pipeline). */
export function compareClientsByCreatedDesc(a, b) {
  const diff = clientTimestampMs(b?.created_at) - clientTimestampMs(a?.created_at);
  if (diff) return diff;
  return String(a?.name || "").localeCompare(String(b?.name || ""));
}

/**
 * Newest first by recent work / change (engagements).
 * Prefers updated_at, then stage change, engaged_at, created_at.
 */
export function compareClientsByRecentWorkDesc(a, b) {
  const aTs = Math.max(
    clientTimestampMs(a?.updated_at),
    clientTimestampMs(a?.engagement_stage_changed_at),
    clientTimestampMs(a?.engaged_at),
    clientTimestampMs(a?.proposal_updated_at),
    clientTimestampMs(a?.created_at),
  );
  const bTs = Math.max(
    clientTimestampMs(b?.updated_at),
    clientTimestampMs(b?.engagement_stage_changed_at),
    clientTimestampMs(b?.engaged_at),
    clientTimestampMs(b?.proposal_updated_at),
    clientTimestampMs(b?.created_at),
  );
  const diff = bTs - aTs;
  if (diff) return diff;
  return String(a?.name || "").localeCompare(String(b?.name || ""));
}

function projectDateMs(client) {
  return Math.max(
    clientTimestampMs(client?.timeline_start),
    clientTimestampMs(client?.engaged_at),
    clientTimestampMs(client?.start_date),
    clientTimestampMs(client?.created_at),
  );
}

/** True when a project timeline is still open (or monthly / undated). */
export function isCurrentProject(client) {
  if (!client) return false;
  if ((client.engagement_type || "project") === "monthly") return true;
  const end = client.timeline_end;
  if (!end) return true;
  const endMs = clientTimestampMs(end);
  if (!endMs) return true;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return endMs >= today.getTime();
}

/**
 * Studio list: current projects first, then newest → oldest by start / engaged date.
 */
export function compareClientsByProjectDateDesc(a, b) {
  const aCurrent = isCurrentProject(a) ? 1 : 0;
  const bCurrent = isCurrentProject(b) ? 1 : 0;
  if (aCurrent !== bCurrent) return bCurrent - aCurrent;
  const diff = projectDateMs(b) - projectDateMs(a);
  if (diff) return diff;
  return String(a?.name || "").localeCompare(String(b?.name || ""));
}
